import React from 'react';

interface SpecificKPIGaugeChartProps {
  score: number; // Score from 1 to 4
  kpiName: string;
  className?: string;
}

const SpecificKPIGaugeChart: React.FC<SpecificKPIGaugeChartProps> = ({
  score,
  kpiName,
  className = '',
}) => {
  // Get color based on score (1-4 scale with center at 2.5)
  const getScoreColor = (score: number) => {
    if (score > 3) return '#6AC982'; // Green
    if (score >= 2.5) return '#F59E0B'; // Yellow
    return '#EF4444'; // Red
  };

  // Calculate the full circle gauge parameters
  const radius = 35;
  const circumference = 2 * Math.PI * radius;

  // Calculate progress based on distance from center point (2.5)
  // Score 2.5 = 0% progress, Score 1 or 4 = 100% progress
  const centerPoint = 2.5;
  const maxDistance = 1.5; // Maximum distance from center (either 1 or 4)
  const distanceFromCenter = Math.abs(score - centerPoint);
  const progressPercentage = Math.min(distanceFromCenter / maxDistance, 1); // 0 to 1
  const progressLength = progressPercentage * circumference;

  // Set up stroke-dasharray
  const strokeDasharray = `${progressLength} ${circumference}`;

  // Calculate rotation: start from 12 o'clock (-90 degrees)
  // For scores > 2.5: rotate clockwise
  // For scores < 2.5: rotate counterclockwise
  let rotation = -90; // Start at 12 o'clock
  if (score < centerPoint) {
    // For scores below center, rotate counterclockwise
    rotation = -90 - progressPercentage * 360;
  }

  return (
    <div className={`relative flex flex-col items-center ${className}`}>
      {/* SVG Gauge Chart */}
      <div className="relative w-20 h-20">
        <svg className="w-full h-full" viewBox="0 0 100 100">
          {/* Background circle */}
          <circle
            cx="50"
            cy="50"
            r={radius}
            fill="none"
            stroke="#E5E7EB"
            strokeWidth="6"
          />

          {/* Progress circle */}
          <circle
            cx="50"
            cy="50"
            r={radius}
            fill="none"
            stroke={getScoreColor(score)}
            strokeWidth="6"
            strokeLinecap="round"
            strokeDasharray={strokeDasharray}
            strokeDashoffset="0"
            className="transition-all duration-1000 ease-out"
            transform={`rotate(${rotation} 50 50)`}
          />
        </svg>

        {/* Score in the center */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-xs font-semibold text-fake-black">
            {score.toFixed(1)}
          </div>
        </div>
      </div>

      {/* KPI name */}
      <div className="text-xs text-fake-black text-center max-w-24 truncate">
        {kpiName}
      </div>
    </div>
  );
};

export default SpecificKPIGaugeChart;
